"use client";

import { useRef } from "react";
import { ArrowRightIcon } from "lucide-react";
import { SuperLink } from "@/components/common/super-link";
import { createPath } from "@/service/routing/routing";
import { Button } from "@/components/ui/button";

const CountBadge = ({ count }: { count: number }) => (
  <div className="flex size-6 shrink-0 items-center justify-center rounded-md bg-zinc-100">
    <span className="text-xs font-medium text-zinc-500">{count}</span>
  </div>
);

const Chip = ({
  href,
  color,
  title,
  count,
}: {
  href: string;
  color: string;
  title: string;
  count: number;
}) => (
  <SuperLink
    href={href}
    draggable={false}
    className="flex w-[220px] shrink-0 items-center gap-1.5 rounded-lg border border-zinc-200 bg-white px-4 py-2"
  >
    <div
      className="size-4 shrink-0 rounded-[4px]"
      style={{ backgroundColor: color }}
    />
    <span className="flex-1 truncate text-sm font-semibold text-zinc-900">
      {title}
    </span>
    <CountBadge count={count} />
  </SuperLink>
);

export const ExamSavedQuestionsSummary = ({
  totalCount,
  flags,
}: {
  totalCount: number;
  flags: ExamQuestionFlagVM[];
}) => {
  const base = createPath("exam.favourite.question");

  // Drag-to-scroll myszką (touch/trackpad działa natywnie). `moved` blokuje klik linku po przeciągnięciu.
  const scrollRef = useRef<HTMLDivElement>(null);
  const drag = useRef({ down: false, startX: 0, startScroll: 0, moved: false });

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    const el = scrollRef.current;
    if (!el) return;
    drag.current = {
      down: true,
      startX: e.clientX,
      startScroll: el.scrollLeft,
      moved: false,
    };
  };

  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const el = scrollRef.current;
    if (!el || !drag.current.down) return;
    const dx = e.clientX - drag.current.startX;
    if (Math.abs(dx) > 4) drag.current.moved = true;
    el.scrollLeft = drag.current.startScroll - dx;
  };

  const endDrag = () => {
    drag.current.down = false;
  };

  const onClickCapture = (e: React.MouseEvent<HTMLDivElement>) => {
    if (drag.current.moved) {
      e.preventDefault();
      e.stopPropagation();
      drag.current.moved = false;
    }
  };

  return (
    <div className="mt-6 flex flex-col gap-4">
      <div className="flex items-center justify-between gap-4">
        <div className="flex min-w-0 flex-col gap-0.5">
          <h2 className="text-lg font-semibold tracking-tight text-zinc-900">
            Zapisane pytania
          </h2>
        </div>
        <Button variant="outline" size="sm" asChild>
          <SuperLink href={base}>
            Zapisane pytania
            <ArrowRightIcon />
          </SuperLink>
        </Button>
      </div>

      {/* Slider ze wszystkimi tagami — przewijany poziomo (touch/trackpad) + drag myszką. */}
      <div
        ref={scrollRef}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerLeave={endDrag}
        onClickCapture={onClickCapture}
        onDragStart={(e) => e.preventDefault()}
        className="no-scrollbar flex cursor-grab gap-2 overflow-x-auto select-none active:cursor-grabbing"
      >
        <Chip
          href={base}
          color="#18181b"
          title="Wszystkie"
          count={totalCount}
        />
        {flags.map((flag) => (
          <Chip
            key={flag.id}
            href={`${base}?flagId=${flag.id}`}
            color={flag.color}
            title={flag.title}
            count={flag.questionCount}
          />
        ))}
      </div>
    </div>
  );
};
